import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Box, Paper, Typography, Button, Chip, Divider,
  Radio, RadioGroup, FormControlLabel, Alert, TextField,
  Tab, Tabs, IconButton, Tooltip as MuiTooltip,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import TuneIcon from "@mui/icons-material/Tune";
import ReplayIcon from "@mui/icons-material/Replay";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Plot from "react-plotly.js";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { MOCK_ZONAS } from "../../api/mockCalidadDatos";
import api, { axiosPrivate } from "../../api/axios";
import PageBody from "../GeneralComponents/PageBody";
import PageButtonBlack from "../GeneralComponents/PageButtonBlack";
import InputFilter from "../GeneralComponents/InputFilter";
import HourglassLoader from "../GeneralComponents/HourglassLoader";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ_CHILE = "America/Santiago";

const LINE_COLORS = [
  "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd",
  "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf",
];
const BAND_COLORS = [
  "rgba(31,119,180,0.15)",  "rgba(255,127,14,0.15)",  "rgba(44,160,44,0.15)",
  "rgba(214,39,40,0.15)",   "rgba(148,103,189,0.15)", "rgba(140,86,75,0.15)",
  "rgba(227,119,194,0.15)", "rgba(127,127,127,0.15)", "rgba(188,189,34,0.15)",
  "rgba(23,190,207,0.15)",
];

const toDateStr      = (d) => d.toISOString().split("T")[0];
const daysAgo        = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return toDateStr(d); };
const monthLastDay   = (ym) => { const [y, m] = ym.split("-").map(Number); return toDateStr(new Date(y, m, 0)); };
const monthsAgoFirst = (n) => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - n); return toDateStr(d); };
const monthsBetween  = (a, b) => { const [ay, am] = a.slice(0, 7).split("-").map(Number); const [by, bm] = b.slice(0, 7).split("-").map(Number); return (by - ay) * 12 + (bm - am); };

// Texto del tooltip de ayuda del selector de granularidad.
const GRAN_HELP = "Corresponde al tamaño de la ventana de tiempo utilizada para agrupar los datos y calcular un resultado. Por ejemplo, una granularidad de 7 días significa que se consideran los datos de un período de siete días y, con ellos, se calcula el promedio correspondiente.";

// Oculta el filtro de Ventana. Con el filtro oculto se consulta siempre "dia".
const SHOW_VENTANA_FILTER = false;

const GRAN_CONFIG = {
  "15m": {
    label: "15 min",
    presets: [
      { label: "Hoy",  compute: () => { const t = toDateStr(new Date()); return { ini: t, fin: t }; } },
      { label: "Ayer", compute: () => { const t = daysAgo(1);            return { ini: t, fin: t }; } },
    ],
    getDefaultRange: () => { const t = toDateStr(new Date()); return { ini: t, fin: t }; },
    apiUrl: "/service/reportes/supervision/cq",
    apiGran: "15m",
  },
  "1h": {
    label: "1 hora",
    presets: [
      { label: "Hoy",  compute: () => { const t = toDateStr(new Date()); return { ini: t, fin: t }; } },
      { label: "Ayer", compute: () => { const t = daysAgo(1);            return { ini: t, fin: t }; } },
    ],
    getDefaultRange: () => { const t = toDateStr(new Date()); return { ini: t, fin: t }; },
    apiUrl: "/service/reportes/supervision/cq",
    apiGran: "1h",
  },
  "1d": {
    label: "1 día",
    presets: [
      { label: "3 días",   compute: () => ({ ini: daysAgo(3),  fin: toDateStr(new Date()) }) },
      { label: "1 semana", compute: () => ({ ini: daysAgo(7),  fin: toDateStr(new Date()) }) },
      { label: "1 mes",    compute: () => ({ ini: daysAgo(30), fin: toDateStr(new Date()) }) },
      { label: "3 meses",  compute: () => ({ ini: daysAgo(90), fin: toDateStr(new Date()) }) },
    ],
    getDefaultRange: () => ({ ini: daysAgo(3), fin: toDateStr(new Date()) }),
    apiUrl: "/service/reportes/supervision/cq",
    apiGran: "1d",
  },
  "7d": {
    label: "7 días",
    presets: [
      { label: "1 mes",    compute: () => ({ ini: monthsAgoFirst(1), fin: toDateStr(new Date()) }) },
      { label: "3 meses",  compute: () => ({ ini: monthsAgoFirst(3), fin: toDateStr(new Date()) }) },
      { label: "6 meses",  compute: () => ({ ini: monthsAgoFirst(6), fin: toDateStr(new Date()) }) },
    ],
    getDefaultRange: () => ({ ini: monthsAgoFirst(3), fin: toDateStr(new Date()) }),
    apiUrl: "/service/reportes/supervision/cq",
    apiGran: "7d",
  },
  "30d": {
    label: "1 mes",
    presets: [
      { label: "1 mes",    compute: () => ({ ini: monthsAgoFirst(1), fin: toDateStr(new Date()) }) },
      { label: "3 meses",  compute: () => ({ ini: monthsAgoFirst(3), fin: toDateStr(new Date()) }) },
      { label: "6 meses",  compute: () => ({ ini: monthsAgoFirst(6), fin: toDateStr(new Date()) }) },
    ],
    getDefaultRange: () => ({ ini: monthsAgoFirst(6), fin: toDateStr(new Date()) }),
    apiUrl: "/service/reportes/supervision/cq",
    apiGran: "30d",
  },
};

const METRICAS = [
  { key: "subida",          label: "Tráfico promedio de subida",           unit: "Mbps", description: "Distribución estadística del tráfico promedio de subida por operador en el período seleccionado.", convert: (v) => v / 1e6 },
  { key: "bajada",          label: "Tráfico promedio de bajada",           unit: "Mbps", description: "Distribución estadística del tráfico promedio de bajada por operador en el período seleccionado.", convert: (v) => v / 1e6 },
  { key: "interac_bajada",  label: "Tráfico bajada internacional",         unit: "Mbps", description: "Distribución estadística del tráfico de bajada internacional por operador.",                        convert: (v) => v / 1e6 },
  { key: "latencia",        label: "Latencia",                             unit: "ms",   description: "Distribución estadística de la latencia promedio por operador en el período seleccionado.",         convert: (v) => v },
  { key: "perdida_paquetes",label: "Pérdida de paquetes",                  unit: "%",    description: "Distribución estadística de la pérdida de paquetes por operador en el período seleccionado.",       convert: (v) => v / 100 },
];

const VISUALIZACIONES = [
  { key: "boxplot_evol",  label: "Cajas y bigotes - evolución" },
  { key: "boxplot_comp",  label: "Cajas y bigotes - comparar" },
  { key: "linea_mediana", label: "Serie temporal - mediana + IQR" },
  { key: "linea_media",   label: "Serie temporal - media ± σ" },
];

const GROUP_DIM_OPTIONS = [
  { key: "zona",         label: "Zona"       },
  { key: "operador",     label: "Operador"   },
  { key: "global",       label: "Global"     },
  { key: "zona_tec",     label: "Zona/Tec"   },
  { key: "operador_tec", label: "Op/Tec"     },
  { key: "global_tec",   label: "Global/Tec" },
];

// Filtros que se desactivan según la dimensión seleccionada (tab GRUPO).
const DIM_DISABLED = {
  zona:         { operador: false, zona: false, tec: true  },
  operador:     { operador: false, zona: true,  tec: true  },
  global:       { operador: true,  zona: true,  tec: true  },
  zona_tec:     { operador: false, zona: false, tec: false },
  operador_tec: { operador: false, zona: true,  tec: false },
  global_tec:   { operador: true,  zona: true,  tec: false },
};
const NO_DISABLED = { operador: false, zona: false, tec: false };

const MAX_RBDS = 5;

function normalizeBucketFecha(fechaBucket, apiGran) {
  if (typeof fechaBucket !== "string") return fechaBucket;
  if (apiGran !== "15m" && apiGran !== "1h") return fechaBucket.slice(0, 10);
  if (!/\d{2}:\d{2}/.test(fechaBucket)) return fechaBucket;
  const d = dayjs.utc(fechaBucket);
  return d.isValid() ? d.tz(TZ_CHILE).format("YYYY-MM-DDTHH:mm:ss") : fechaBucket;
}

// Adapts one API row (dimension=operador) to the internal format.
// The API provides p50_aprox and p95_aprox; p25/p75 are approximated.
function adaptApiRow(row, apiGran) {
  const ap25 = (mn, p50) => (mn != null && p50 != null) ? (mn + p50) / 2 : null;
  const ap75 = (p50, p95) => (p50 != null && p95 != null) ? (p50 + p95) / 2 : null;
  const ib = (col) => row[`internac_bajada_${col}`] ?? row[`interac_bajada_${col}`] ?? null;
  const ib_mean = ib("dia_mean");
  const ib_min  = ib("dia_min");
  const ib_max  = ib("dia_max");
  const ib_p50  = ib("dia_p50_aprox") ?? ib_mean;
  const ib_p25  = ib("dia_p25_aprox");
  const ib_p75  = ib("dia_p75_aprox");
  const ib_p95  = ib("dia_p95_aprox");
  return {
    fecha:          normalizeBucketFecha(row.fecha_bucket, apiGran),
    id_operador:    row.id_operador,
    id_zona:        row.id_zona,
    rbd:            row.rbd,
    tipo_tecnologia: row.tipo_tecnologia,
    // bajada (Bps)
    bajada_dia_mean: row.bajada_dia_mean,
    bajada_dia_std:  null,
    bajada_dia_min:  row.bajada_dia_min,
    bajada_dia_p25:  ap25(row.bajada_dia_min,        row.bajada_dia_p50_aprox),
    bajada_dia_p50:  row.bajada_dia_p50_aprox,
    bajada_dia_p75:  ap75(row.bajada_dia_p50_aprox,  row.bajada_dia_p95_aprox),
    bajada_dia_max:  row.bajada_dia_max,
    bajada_ven_mean: null, bajada_ven_std: null, bajada_ven_min: null,
    bajada_ven_p25: null, bajada_ven_p50: null, bajada_ven_p75: null, bajada_ven_max: null,
    // subida (Bps)
    subida_dia_mean: row.subida_dia_mean,
    subida_dia_std:  null,
    subida_dia_min:  row.subida_dia_min,
    subida_dia_p25:  ap25(row.subida_dia_min,        row.subida_dia_p50_aprox),
    subida_dia_p50:  row.subida_dia_p50_aprox,
    subida_dia_p75:  ap75(row.subida_dia_p50_aprox,  row.subida_dia_p95_aprox),
    subida_dia_max:  row.subida_dia_max,
    subida_ven_mean: null, subida_ven_std: null, subida_ven_min: null,
    subida_ven_p25: null, subida_ven_p50: null, subida_ven_p75: null, subida_ven_max: null,
    // latencia (ms)
    latencia_dia_mean: row.latencia_dia_mean,
    latencia_dia_std:  null,
    latencia_dia_min:  row.latencia_dia_min,
    latencia_dia_p25:  ap25(row.latencia_dia_min,       row.latencia_dia_p50_aprox ?? row.latencia_dia_mean),
    latencia_dia_p50:  row.latencia_dia_p50_aprox ?? row.latencia_dia_mean ?? null,
    latencia_dia_p75:  ap75(row.latencia_dia_p50_aprox ?? row.latencia_dia_mean, row.latencia_dia_p95_aprox),
    latencia_dia_max:  row.latencia_dia_max,
    latencia_ven_mean: null, latencia_ven_std: null, latencia_ven_min: null,
    latencia_ven_p25: null, latencia_ven_p50: null, latencia_ven_p75: null, latencia_ven_max: null,
    // perdida_paquetes (raw)
    perdida_paquetes_dia_mean: row.perdida_paquetes_dia_mean,
    perdida_paquetes_dia_std:  null,
    perdida_paquetes_dia_min:  row.perdida_paquetes_dia_min,
    perdida_paquetes_dia_p25:  null,
    perdida_paquetes_dia_p50:  null,
    perdida_paquetes_dia_p75:  null,
    perdida_paquetes_dia_max:  row.perdida_paquetes_dia_max,
    perdida_paquetes_ven_mean: null, perdida_paquetes_ven_std: null, perdida_paquetes_ven_min: null,
    perdida_paquetes_ven_p25: null, perdida_paquetes_ven_p50: null, perdida_paquetes_ven_p75: null, perdida_paquetes_ven_max: null,
    interac_bajada_dia_mean: ib_mean,
    interac_bajada_dia_std:  null,
    interac_bajada_dia_min:  ib_min,
    interac_bajada_dia_p25:  ib_p25 ?? ap25(ib_min, ib_p50),
    interac_bajada_dia_p50:  ib_p50,
    interac_bajada_dia_p75:  ib_p75 ?? ap75(ib_p50, ib_p95),
    interac_bajada_dia_max:  ib_max,
    interac_bajada_ven_mean: null, interac_bajada_ven_p25: null, interac_bajada_ven_p50: null,
    interac_bajada_ven_p75: null, interac_bajada_ven_min: null, interac_bajada_ven_max: null,
  };
}

// ── trace builders ─────────────────────────────────────────────────────────

function buildBoxPlotTraces(rows, metricKey, ventana, convert, getKey, getLabel) {
  const prefix = `${metricKey}_${ventana}`;
  const P = (col) => `${prefix}_${col}`;
  const byEntityDate = {};
  rows.forEach((r) => {
    const ek = getKey(r);
    const k = `${ek}||${r.fecha}`;
    if (!byEntityDate[k]) byEntityDate[k] = { ek, fecha: r.fecha, vals: [] };
    const p50 = r[P("p50")];
    if (p50 != null) byEntityDate[k].vals.push({ p25: r[P("p25")], p50, p75: r[P("p75")], mn: r[P("min")], mx: r[P("max")] });
  });
  const entityData = {};
  Object.values(byEntityDate).forEach(({ ek, fecha, vals }) => {
    if (!vals.length) return;
    if (!entityData[ek]) entityData[ek] = [];
    const avg = (arr) => { const a = arr.filter((v) => v != null); return a.length ? a.reduce((x, y) => x + y, 0) / a.length : null; };
    const c = convert;
    const p25 = avg(vals.map((v) => v.p25));
    const p50 = avg(vals.map((v) => v.p50));
    const p75 = avg(vals.map((v) => v.p75));
    const mn  = vals.map((v) => v.mn).filter((v) => v != null && isFinite(v));
    const mx  = vals.map((v) => v.mx).filter((v) => v != null && isFinite(v));
    entityData[ek].push({ x: fecha, p25: p25 != null ? c(p25) : null, p50: c(p50), p75: p75 != null ? c(p75) : null, mn: mn.length ? c(Math.min(...mn)) : null, mx: mx.length ? c(Math.max(...mx)) : null });
  });
  return Object.entries(entityData).map(([ek, pts], i) => {
    const sorted = pts.sort((a, b) => a.x.localeCompare(b.x));
    const color = LINE_COLORS[i % LINE_COLORS.length];
    return { type: "box", name: getLabel(ek), x: sorted.map((p) => p.x), q1: sorted.map((p) => p.p25), median: sorted.map((p) => p.p50), q3: sorted.map((p) => p.p75), lowerfence: sorted.map((p) => p.mn), upperfence: sorted.map((p) => p.mx), marker: { color }, boxmean: false };
  });
}

function buildBoxCompTraces(rows, metricKey, ventana, convert, getKey, getLabel) {
  const prefix = `${metricKey}_${ventana}`;
  const P = (col) => `${prefix}_${col}`;
  const byEntity = {};
  rows.forEach((r) => {
    const ek = getKey(r);
    const p50 = r[P("p50")];
    if (p50 == null) return;
    if (!byEntity[ek]) byEntity[ek] = { p25s: [], p50s: [], p75s: [], mins: [], maxs: [] };
    const d = byEntity[ek];
    if (r[P("p25")] != null) d.p25s.push(r[P("p25")]);
    d.p50s.push(p50);
    if (r[P("p75")] != null) d.p75s.push(r[P("p75")]);
    const mn = r[P("min")]; if (mn != null && isFinite(mn)) d.mins.push(mn);
    const mx = r[P("max")]; if (mx != null && isFinite(mx)) d.maxs.push(mx);
  });
  const avg = (arr) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
  return Object.entries(byEntity).map(([ek, d], i) => {
    const color = LINE_COLORS[i % LINE_COLORS.length];
    const c = convert;
    const p50agg = avg(d.p50s); if (p50agg == null) return null;
    const name = getLabel(ek);
    return { type: "box", name, x: [name], q1: d.p25s.length ? [c(avg(d.p25s))] : [null], median: [c(p50agg)], q3: d.p75s.length ? [c(avg(d.p75s))] : [null], lowerfence: d.mins.length ? [c(Math.min(...d.mins))] : [null], upperfence: d.maxs.length ? [c(Math.max(...d.maxs))] : [null], marker: { color } };
  }).filter(Boolean);
}

function buildLineMedianTraces(rows, metricKey, ventana, convert, getKey, getLabel) {
  const prefix = `${metricKey}_${ventana}`;
  const P = (col) => `${prefix}_${col}`;
  const byEntityDate = {};
  rows.forEach((r) => {
    const ek = getKey(r);
    const k = `${ek}||${r.fecha}`;
    if (!byEntityDate[k]) byEntityDate[k] = { ek, fecha: r.fecha, p25s: [], p50s: [], p75s: [] };
    if (r[P("p50")] != null) { byEntityDate[k].p25s.push(r[P("p25")]); byEntityDate[k].p50s.push(r[P("p50")]); byEntityDate[k].p75s.push(r[P("p75")]); }
  });
  const entityData = {};
  Object.values(byEntityDate).forEach(({ ek, fecha, p25s, p50s, p75s }) => {
    if (!p50s.length) return;
    if (!entityData[ek]) entityData[ek] = { x: [], p25: [], p50: [], p75: [] };
    const avg = (arr) => { const a = arr.filter((v) => v != null); return a.length ? a.reduce((x, y) => x + y, 0) / a.length : null; };
    entityData[ek].x.push(fecha);
    entityData[ek].p25.push(avg(p25s) != null ? convert(avg(p25s)) : null);
    entityData[ek].p50.push(convert(avg(p50s)));
    entityData[ek].p75.push(avg(p75s) != null ? convert(avg(p75s)) : null);
  });
  const traces = [];
  Object.entries(entityData).forEach(([ek, d], i) => {
    const sorted = d.x.map((x, j) => ({ x, p25: d.p25[j], p50: d.p50[j], p75: d.p75[j] })).sort((a, b) => a.x.localeCompare(b.x));
    const color = LINE_COLORS[i % LINE_COLORS.length];
    const bc    = BAND_COLORS[i % BAND_COLORS.length];
    const name  = getLabel(ek);
    const hasIqr = sorted.some((s) => s.p25 != null && s.p75 != null);
    if (hasIqr) {
      traces.push({ type: "scatter", x: [...sorted.map((s) => s.x), ...sorted.map((s) => s.x).reverse()], y: [...sorted.map((s) => s.p75), ...sorted.map((s) => s.p25).reverse()], fill: "toself", fillcolor: bc, line: { color: "rgba(0,0,0,0)" }, showlegend: false, hoverinfo: "skip" });
    }
    traces.push({ type: "scatter", mode: "lines+markers", name, x: sorted.map((s) => s.x), y: sorted.map((s) => s.p50), line: { color, width: 2 }, marker: { size: 4, color } });
  });
  return traces;
}

function buildLineMeanTraces(rows, metricKey, ventana, convert, getKey, getLabel) {
  const prefix = `${metricKey}_${ventana}`;
  const meanCol = `${prefix}_mean`;
  const stdCol  = `${prefix}_std`;
  const byEntityDate = {};
  rows.forEach((r) => {
    const ek = getKey(r);
    const k = `${ek}||${r.fecha}`;
    if (!byEntityDate[k]) byEntityDate[k] = { ek, fecha: r.fecha, means: [], stds: [] };
    if (r[meanCol] != null) { byEntityDate[k].means.push(r[meanCol]); byEntityDate[k].stds.push(r[stdCol] ?? 0); }
  });
  const entityData = {};
  Object.values(byEntityDate).forEach(({ ek, fecha, means, stds }) => {
    if (!means.length) return;
    if (!entityData[ek]) entityData[ek] = { x: [], mean: [], std: [] };
    const avg = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;
    entityData[ek].x.push(fecha); entityData[ek].mean.push(convert(avg(means))); entityData[ek].std.push(convert(avg(stds)));
  });
  const traces = [];
  Object.entries(entityData).forEach(([ek, d], i) => {
    const sorted = d.x.map((x, j) => ({ x, mean: d.mean[j], std: d.std[j] })).sort((a, b) => a.x.localeCompare(b.x));
    const color = LINE_COLORS[i % LINE_COLORS.length];
    const bc    = BAND_COLORS[i % BAND_COLORS.length];
    const name  = getLabel(ek);
    const hi = sorted.map((s) => s.mean + s.std);
    const lo = sorted.map((s) => Math.max(0, s.mean - s.std));
    if (sorted.some((s) => s.std > 0)) {
      traces.push({ type: "scatter", x: [...sorted.map((s) => s.x), ...sorted.map((s) => s.x).reverse()], y: [...hi, ...lo.reverse()], fill: "toself", fillcolor: bc, line: { color: "rgba(0,0,0,0)" }, showlegend: false, hoverinfo: "skip" });
    }
    traces.push({ type: "scatter", mode: "lines+markers", name, x: sorted.map((s) => s.x), y: sorted.map((s) => s.mean), line: { color, width: 2 }, marker: { size: 4, color } });
  });
  return traces;
}

function exportXlsx(rows, metricKey, ventana, convert, unit, operators) {
  const prefix = `${metricKey}_${ventana}`;
  const P = (col) => `${prefix}_${col}`;
  const data = rows.filter((r) => r[P("p50")] != null || r[P("mean")] != null).map((r) => {
    const op = operators.find((o) => o.id_operador === r.id_operador);
    return {
      fecha: r.fecha, operador: op?.nombre_operador ?? (typeof r.id_operador === "string" ? r.id_operador : `Op ${r.id_operador}`),
      [`min (${unit})`]:  r[P("min")]  != null ? Number(convert(r[P("min")]).toFixed(4))  : null,
      [`p25 (${unit})`]:  r[P("p25")]  != null ? Number(convert(r[P("p25")]).toFixed(4))  : null,
      [`p50 (${unit})`]:  r[P("p50")]  != null ? Number(convert(r[P("p50")]).toFixed(4))  : null,
      [`p75 (${unit})`]:  r[P("p75")]  != null ? Number(convert(r[P("p75")]).toFixed(4))  : null,
      [`max (${unit})`]:  r[P("max")]  != null ? Number(convert(r[P("max")]).toFixed(4))  : null,
      [`mean (${unit})`]: r[P("mean")] != null ? Number(convert(r[P("mean")]).toFixed(4)) : null,
    };
  });
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, metricKey);
  XLSX.writeFile(wb, `calidad_${metricKey}_${ventana}.xlsx`);
}

const downloadBtnSx = {
  border: "2px solid #003B8D", borderRadius: "4px", color: "#003B8D",
  backgroundColor: "#FFF", textTransform: "none", fontSize: "13px",
  padding: "6px 16px", fontWeight: 500, "&:hover": { backgroundColor: "#f0f4ff" },
};

export default function CalidadDatos() {
  const [operators,     setOperators]     = useState([]);
  const [tecnologias,   setTecnologias]   = useState([]);
  const [apiData,       setApiData]       = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [reloadKey,     setReloadKey]     = useState(0);

  const [granularity,  setGranularity]  = useState("1d");
  const [periodoTab,   setPeriodoTab]   = useState("ultimo");
  const [fechaInicio,  setFechaInicio]  = useState(() => daysAgo(3));
  const [fechaFin,     setFechaFin]     = useState(() => toDateStr(new Date()));
  // Fechas en edición del "Periodo abierto"; se confirman con el botón Aplicar.
  const [draftInicio,  setDraftInicio]  = useState(() => daysAgo(3));
  const [draftFin,     setDraftFin]     = useState(() => toDateStr(new Date()));
  const [selectedOps,   setSelectedOps]   = useState([]);
  const [selectedZona,  setSelectedZona]  = useState(null);
  const [selectedTec,   setSelectedTec]   = useState(null);
  const [ventana,            setVentana]            = useState("dia");
  const [metricaKey,         setMetricaKey]         = useState("subida");
  const [visualizacion,      setVisualizacion]      = useState("boxplot_evol");
  const [selectedDimension,  setSelectedDimension]  = useState("operador");
  const [filterTab,    setFilterTab]    = useState("grupo");
  const [rbdInput,     setRbdInput]     = useState("");
  const [selectedRbds, setSelectedRbds] = useState([]);

  // Load operators once
  useEffect(() => {
    axiosPrivate({ method: "GET", url: "/service/admin/get_operadores/", showSnackbarOnError: false })
      .then((res) => {
        const ops = res.data ?? [];
        setOperators(ops);
        setSelectedOps(ops.map((o) => o.id_operador));
      })
      .catch(() => {});
  }, []);

  // Load technology types once (igual que en FAS)
  useEffect(() => {
    api.getTechTypes()
      .then((res) => setTecnologias((res.data ?? []).map((t) => ({ id: t.id_tipo_tecnologia, label: t.nombre_enlace }))))
      .catch(() => {});
  }, []);

  const granConfig = GRAN_CONFIG[granularity];

  // Tracks which RBDs are already loaded for the current period signature.
  const rbdLoadRef = useRef({ sig: "", loaded: [] });

  // Fetch stats for GRUPO mode (single call replacing the dataset).
  useEffect(() => {
    if (filterTab !== "grupo") return;
    const cfg = GRAN_CONFIG[granularity];
    setLoading(true);
    setError(null);
    axiosPrivate({
      method: "GET",
      url: cfg.apiUrl,
      params: {
        granularidad: cfg.apiGran,
        dimension: selectedDimension,
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
        ...(selectedZona != null && selectedZona !== "all" ? { id_zona:        Number(selectedZona) } : {}),
        ...(selectedTec  != null && selectedTec  !== "all" ? { tipo_tecnologia: Number(selectedTec) } : {}),
        ...(selectedOps.length === 1                       ? { id_operador:     selectedOps[0] }      : {}),
      },
      showSnackbarOnError: false,
    })
      .then((res) => setApiData((res.data ?? []).map((r) => adaptApiRow(r, cfg.apiGran))))
      .catch(() => setError("Error al cargar los datos."))
      .finally(() => setLoading(false));
  }, [granularity, fechaInicio, fechaFin, selectedZona, selectedTec, selectedOps, selectedDimension, filterTab, reloadKey]);

  // Fetch stats for RBD mode, accumulating one call per RBD: adding an RBD
  // fetches only that one and appends it; removing one drops it from the
  // loaded data; changing period/granularity reloads all selected RBDs.
  useEffect(() => {
    if (filterTab !== "rbd") {
      rbdLoadRef.current = { sig: "", loaded: [] };
      return;
    }
    const cfg = GRAN_CONFIG[granularity];
    const sig = `${granularity}|${fechaInicio}|${fechaFin}`;
    const prev = rbdLoadRef.current;

    // La API cq espera el parámetro del RBD como "rbds".
    const fetchRbd = (rbd) =>
      axiosPrivate({
        method: "GET",
        url: cfg.apiUrl,
        params: { granularidad: cfg.apiGran, dimension: "rbd", fecha_inicio: fechaInicio, fecha_fin: fechaFin, rbds: rbd },
        showSnackbarOnError: false,
      }).then((res) => (res.data ?? []).map((r) => adaptApiRow(r, cfg.apiGran)));

    if (selectedRbds.length === 0) {
      rbdLoadRef.current = { sig, loaded: [] };
      setApiData([]);
      setError(null);
      setLoading(false);
      return;
    }

    if (prev.sig !== sig) {
      // Period/granularity changed: reload every selected RBD from scratch.
      setLoading(true);
      setError(null);
      Promise.all(selectedRbds.map(fetchRbd))
        .then((results) => setApiData(results.flat()))
        .catch(() => setError("Error al cargar los datos."))
        .finally(() => setLoading(false));
      rbdLoadRef.current = { sig, loaded: [...selectedRbds] };
      return;
    }

    const added   = selectedRbds.filter((r) => !prev.loaded.includes(r));
    const removed  = prev.loaded.filter((r) => !selectedRbds.includes(r));
    if (removed.length) setApiData((data) => data.filter((row) => !removed.includes(String(row.rbd))));
    if (added.length) {
      setLoading(true);
      setError(null);
      Promise.all(added.map(fetchRbd))
        .then((results) => setApiData((data) => [...data, ...results.flat()]))
        .catch(() => setError("Error al cargar los datos."))
        .finally(() => setLoading(false));
    }
    rbdLoadRef.current = { sig, loaded: [...selectedRbds] };
  }, [filterTab, granularity, fechaInicio, fechaFin, selectedRbds, reloadKey]);

  const selectedMetrica = METRICAS.find((m) => m.key === metricaKey);

  const allRbds = useMemo(() => [...new Set(apiData.map((r) => r.rbd).filter(Boolean))].sort(), [apiData]);

  const getEntityKey = useCallback((r) => {
    const tec = r.tipo_tecnologia != null ? String(r.tipo_tecnologia) : "";
    const dim = filterTab === "rbd" ? "rbd" : selectedDimension;
    switch (dim) {
      case "zona":         return String(r.id_zona ?? "");
      case "zona_tec":     return `${r.id_zona ?? ""}|${tec}`;
      case "rbd":          return String(r.rbd ?? "");
      case "operador":     return String(r.id_operador ?? "");
      case "operador_tec": return `${r.id_operador ?? ""}|${tec}`;
      case "global_tec":   return tec;
      default:             return "global";
    }
  }, [selectedDimension, filterTab]);

  const getEntityLabel = useCallback((key) => {
    const dim = filterTab === "rbd" ? "rbd" : selectedDimension;
    // La API cq entrega la tecnología ya como string real; sólo anteponemos
    // "Tec" cuando el valor es un id numérico que no está en el catálogo.
    const tecLabelOf = (tecId) => {
      const found = tecnologias.find((t) => String(t.id) === String(tecId));
      if (found) return found.label;
      return /^\d+$/.test(String(tecId)) ? `Tec ${tecId}` : String(tecId);
    };
    switch (dim) {
      case "zona":
        return MOCK_ZONAS.find((z) => String(z.id) === key)?.label ?? `Zona ${key}`;
      case "zona_tec": {
        const [zonaId, tecId] = key.split("|");
        const zonaLabel = MOCK_ZONAS.find((z) => String(z.id) === zonaId)?.label ?? `Zona ${zonaId}`;
        return `${zonaLabel} / ${tecLabelOf(tecId)}`;
      }
      case "rbd":
        return `RBD ${key}`;
      case "operador":
        return operators.find((o) => String(o.id_operador) === key)?.nombre_operador ?? key;
      case "operador_tec": {
        const [opKey, tecId] = key.split("|");
        const opLabel  = operators.find((o) => String(o.id_operador) === opKey)?.nombre_operador ?? opKey;
        return `${opLabel} / ${tecLabelOf(tecId)}`;
      }
      case "global_tec":
        return tecLabelOf(key);
      default:
        return "Global";
    }
  }, [selectedDimension, operators, filterTab, tecnologias]);

  const filteredRows = useMemo(() => {
    if (granularity === "15m" || granularity === "1h" || granularity === "1d") return apiData;
    if (filterTab === "rbd") {
      if (!selectedRbds.length) return apiData;
      return apiData.filter((r) => selectedRbds.includes(String(r.rbd)));
    }
    if (!selectedDimension.startsWith("operador")) return apiData;
    if (!selectedOps.length) return apiData;
    // La API cq agregada (7d/30d) entrega id_operador como nombre del operador,
    // no como id numérico. Aceptamos ambas formas para el filtro por operador.
    const allowedOps = new Set();
    operators.forEach((o) => {
      if (selectedOps.includes(o.id_operador)) {
        allowedOps.add(o.id_operador);
        allowedOps.add(o.nombre_operador);
      }
    });
    return apiData.filter((r) => allowedOps.has(r.id_operador));
  }, [apiData, selectedOps, selectedDimension, granularity, filterTab, selectedRbds, operators]);

  const entityKeys = useMemo(
    () => [...new Set(filteredRows.map(getEntityKey).filter(Boolean))],
    [filteredRows, getEntityKey]
  );

  const traces = useMemo(() => {
    if (!selectedMetrica) return [];
    const { key, convert } = selectedMetrica;
    switch (visualizacion) {
      case "boxplot_evol":  return buildBoxPlotTraces(filteredRows, key, ventana, convert, getEntityKey, getEntityLabel);
      case "boxplot_comp":  return buildBoxCompTraces(filteredRows, key, ventana, convert, getEntityKey, getEntityLabel);
      case "linea_mediana": return buildLineMedianTraces(filteredRows, key, ventana, convert, getEntityKey, getEntityLabel);
      case "linea_media":   return buildLineMeanTraces(filteredRows, key, ventana, convert, getEntityKey, getEntityLabel);
      default:              return buildBoxPlotTraces(filteredRows, key, ventana, convert, getEntityKey, getEntityLabel);
    }
  }, [filteredRows, metricaKey, ventana, visualizacion, selectedMetrica, getEntityKey, getEntityLabel]);

  const isBoxplot = visualizacion.startsWith("boxplot");

  const plotLayout = useMemo(() => ({
    margin: { l: 60, r: 50, t: 50, b: 110 },
    hovermode: "x",
    legend: {
      orientation: "h", yanchor: "top", y: -0.18, xanchor: "center", x: 0.5,
      bgcolor: "rgba(255,255,255,0.7)", bordercolor: "#ddd", borderwidth: 1,
      font: { size: 10 },
    },
    xaxis: { tickfont: { size: 10 }, automargin: true },
    yaxis: { tickfont: { size: 10 }, title: selectedMetrica?.unit ?? "", automargin: true },
    paper_bgcolor: "rgba(0,0,0,0)",
    plot_bgcolor: "#fafafa",
    boxmode: isBoxplot ? "overlay" : undefined,
    height: isBoxplot ? 500 : 460,
  }), [isBoxplot, visualizacion, selectedMetrica]);

  // Las fechas del periodo abierto son de libre selección hasta hoy; la ventana
  // máxima permitida se valida aparte (periodoError) y bloquea el botón Aplicar.
  const maxFechaFin = useMemo(() => toDateStr(new Date()), []);

  // Valida la ventana del periodo abierto: 3 meses para 1d, 6 para 7d/30d.
  const periodoError = useMemo(() => {
    if (periodoTab !== "abierto") return null;
    if (granularity === "15m" || granularity === "1h") return null;
    if (draftFin < draftInicio) return "La fecha 'Hasta' no puede ser anterior a 'Desde'.";
    if (granularity === "1d") {
      const lim = new Date(draftInicio + "T12:00:00");
      lim.setMonth(lim.getMonth() + 3);
      if (new Date(draftFin + "T12:00:00") > lim) {
        return "El rango no puede superar los 3 meses para granularidad de 1 día.";
      }
    } else if (monthsBetween(draftInicio, draftFin) > 6) {
      return "El rango no puede superar los 6 meses para esta granularidad.";
    }
    return null;
  }, [periodoTab, granularity, draftInicio, draftFin]);

  // Confirma las fechas en edición del periodo abierto (dispara la carga).
  const handleAplicarPeriodo = useCallback(() => {
    setFechaInicio(draftInicio);
    setFechaFin(draftFin);
  }, [draftInicio, draftFin]);

  const periodoDirty = draftInicio !== fechaInicio || draftFin !== fechaFin;

  // Reintenta la carga: resetea el control de RBD cargados para forzar una
  // recarga completa y dispara de nuevo el useEffect correspondiente.
  const handleRetry = useCallback(() => {
    rbdLoadRef.current = { sig: "", loaded: [] };
    setReloadKey((k) => k + 1);
  }, []);

  const handleClearFilters = useCallback(() => {
    const { ini, fin } = GRAN_CONFIG["1d"].getDefaultRange();
    setGranularity("1d");
    setPeriodoTab("ultimo");
    setFechaInicio(ini);
    setFechaFin(fin);
    setDraftInicio(ini);
    setDraftFin(fin);
    setSelectedOps(operators.map((o) => o.id_operador));
    setSelectedZona(null);
    setSelectedTec(null);
    setVentana("dia");
    setSelectedDimension("operador");
    setFilterTab("grupo");
    setRbdInput("");
    setSelectedRbds([]);
  }, [operators]);

  const handleAddRbd = useCallback(() => {
    const val = rbdInput.trim();
    if (!val || selectedRbds.includes(val) || selectedRbds.length >= MAX_RBDS) return;
    setSelectedRbds((prev) => [...prev, val]);
    setRbdInput("");
  }, [rbdInput, selectedRbds]);

  const handleRemoveRbd = useCallback((rbd) => {
    setSelectedRbds((prev) => prev.filter((r) => r !== rbd));
  }, []);

  // Removes an entity only from the loaded data (no re-fetch).
  const handleRemoveEntity = useCallback((key) => {
    setApiData((prev) => prev.filter((r) => getEntityKey(r) !== key));
  }, [getEntityKey]);

  const operadorOpts = useMemo(() => [
    { value: "all", label: "Todos los operadores" },
    ...operators.map((o) => ({ value: String(o.id_operador), label: o.nombre_operador })),
  ], [operators]);

  const opFilterValue = !selectedOps.length || selectedOps.length === operators.length
    ? "all"
    : String(selectedOps[0] ?? "all");

  const dimDisabled = DIM_DISABLED[selectedDimension] ?? NO_DISABLED;

  // Resetea los filtros que quedan desactivados al cambiar de dimensión,
  // para que no sigan afectando la consulta.
  useEffect(() => {
    const d = DIM_DISABLED[selectedDimension] ?? NO_DISABLED;
    if (d.operador) setSelectedOps(operators.map((o) => o.id_operador));
    if (d.zona) setSelectedZona(null);
    if (d.tec) setSelectedTec(null);
  }, [selectedDimension, operators]);

  return (
    <PageBody>
      {/* Título y descripción */}
      <Typography variant="h5" fontWeight="bold" textAlign="center"
        sx={{ letterSpacing: 1, textTransform: "uppercase", mb: 1 }}>
        Calidad de Datos
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" mb={3} maxWidth={700} mx="auto">
        Análisis estadístico de calidad de servicio por operador y período. Seleccione la métrica y
        tipo de visualización para explorar la distribución de los datos.
      </Typography>

      <Box sx={{ mx: "-3.5rem", display: "flex", gap: 2 }}>

        {/* ── Sidebar ── */}
        <Box sx={{ width: 300, flexShrink: 0, position: "sticky", top: 16, alignSelf: "flex-start" }}>
          <Paper sx={{ p: 1.5, maxHeight: "calc(100vh - 32px)", overflowY: "auto", overflowX: "hidden",
            pointerEvents: loading ? "none" : "auto", opacity: loading ? 0.6 : 1, transition: "opacity 0.2s" }}>

            {/* ── Filtro de tiempo ── */}
            <Typography variant="h5" mb={1.5}>Filtro de tiempo</Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Granularidad</Typography>
              <MuiTooltip title={GRAN_HELP} arrow placement="top"
                componentsProps={{
                  tooltip: { sx: { bgcolor: "#003B8D", color: "#fff", fontSize: "0.72rem", fontWeight: 500, borderRadius: "8px", px: 1.25, py: 0.75 } },
                  arrow: { sx: { color: "#003B8D" } },
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 15, color: "action.active", cursor: "help" }} />
              </MuiTooltip>
            </Box>
            <Box sx={{ display: "flex", gap: 0, mb: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
              {Object.entries(GRAN_CONFIG).map(([key, cfg], i, arr) => (
                <Button key={key} size="small" disableRipple
                  variant={granularity === key ? "contained" : "text"} color="primary"
                  onClick={() => { const { ini, fin } = GRAN_CONFIG[key].getDefaultRange(); setGranularity(key); setFechaInicio(ini); setFechaFin(fin); setDraftInicio(ini); setDraftFin(fin); }}
                  sx={{ flex: 1, px: 0.3, py: 1, fontSize: "0.68rem", textTransform: "none", borderRadius: 0, borderRight: i < arr.length - 1 ? "1px solid" : "none", borderColor: "divider", boxShadow: "none", minWidth: 0 }}
                >{cfg.label}</Button>
              ))}
            </Box>

            <Tabs value={periodoTab} onChange={(_, v) => { setPeriodoTab(v); if (v === "abierto") { setDraftInicio(fechaInicio); setDraftFin(fechaFin); } }}
              variant="fullWidth"
              sx={{ mb: 1.5, minHeight: 40, borderBottom: "1px solid", borderColor: "divider",
                "& .MuiTabs-scroller": { minHeight: 40 },
                "& .MuiTab-root": { minHeight: 40, py: 1, fontSize: "0.75rem", fontWeight: "bold", textTransform: "uppercase", color: "text.secondary" },
                "& .Mui-selected": { bgcolor: "rgba(25,118,210,0.06)" },
              }}
            >
              <Tab label="Último periodo" value="ultimo" disableRipple />
              <Tab label="Periodo abierto" value="abierto" disableRipple />
            </Tabs>

            {periodoTab === "ultimo" ? (
              <Box sx={{ display: "flex", gap: 0, mb: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                {granConfig.presets.map((p, i) => {
                  const { ini, fin } = p.compute();
                  return (
                    <Button key={p.label} size="small" disableRipple
                      variant={fechaInicio === ini && fechaFin === fin ? "contained" : "text"} color="primary"
                      onClick={() => { setFechaInicio(ini); setFechaFin(fin); }}
                      sx={{ flex: 1, px: 0.3, py: 1, fontSize: "0.68rem", textTransform: "none", borderRadius: 0, borderRight: i < granConfig.presets.length - 1 ? "1px solid" : "none", borderColor: "divider", boxShadow: "none", minWidth: 0 }}
                    >{p.label}</Button>
                  );
                })}
              </Box>
            ) : (
              <Box sx={{ mb: 1.5 }}>
                {(granularity === "15m" || granularity === "1h") && (
                  <TextField label="Fecha" type="date" size="small" fullWidth
                    value={draftInicio} onChange={(e) => { setDraftInicio(e.target.value); setDraftFin(e.target.value); }}
                    InputLabelProps={{ shrink: true }} inputProps={{ max: toDateStr(new Date()) }}
                  />
                )}
                {granularity === "1d" && (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <TextField label="Desde" type="date" size="small" fullWidth value={draftInicio}
                      onChange={(e) => setDraftInicio(e.target.value)}
                      InputLabelProps={{ shrink: true }} inputProps={{ max: maxFechaFin }}
                    />
                    <TextField label="Hasta" type="date" size="small" fullWidth value={draftFin}
                      onChange={(e) => setDraftFin(e.target.value)}
                      InputLabelProps={{ shrink: true }} inputProps={{ max: maxFechaFin }}
                    />
                  </Box>
                )}
                {(granularity === "7d" || granularity === "30d") && (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <TextField label="Desde" type="month" size="small" fullWidth value={draftInicio.slice(0, 7)}
                      onChange={(e) => setDraftInicio(e.target.value + "-01")}
                      InputLabelProps={{ shrink: true }} inputProps={{ max: maxFechaFin.slice(0, 7) }}
                    />
                    <TextField label="Hasta" type="month" size="small" fullWidth value={draftFin.slice(0, 7)}
                      onChange={(e) => { const last = monthLastDay(e.target.value); setDraftFin(last > maxFechaFin ? maxFechaFin : last); }}
                      InputLabelProps={{ shrink: true }} inputProps={{ max: maxFechaFin.slice(0, 7) }}
                    />
                  </Box>
                )}
                {periodoError && (
                  <Alert severity="error" sx={{ mt: 1, py: 0, fontSize: "0.72rem", alignItems: "center", "& .MuiAlert-message": { py: 0.75 } }}>
                    {periodoError}
                  </Alert>
                )}
                <Button variant="contained" color="primary" size="small" fullWidth disableRipple disableElevation
                  onClick={handleAplicarPeriodo} disabled={!periodoDirty || !!periodoError}
                  sx={{ mt: 1.25, textTransform: "none", fontWeight: "bold" }}
                >
                  Aplicar
                </Button>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* ── Filtro por grupo o RBD ── */}
            <Typography variant="h5" mb={1}>Filtro por grupo o RBD</Typography>

            <Tabs value={filterTab} onChange={(_, v) => setFilterTab(v)}
              variant="fullWidth"
              sx={{ mb: 1.5, minHeight: 40, borderBottom: "1px solid", borderColor: "divider",
                "& .MuiTabs-scroller": { minHeight: 40 },
                "& .MuiTab-root": { minHeight: 40, py: 1, fontSize: "0.75rem", fontWeight: "bold", textTransform: "uppercase", color: "text.secondary" },
                "& .Mui-selected": { bgcolor: "rgba(25,118,210,0.06)" },
              }}
            >
              <Tab label="GRUPO" value="grupo" disableRipple />
              <Tab label="RBD" value="rbd" disableRipple />
            </Tabs>

            {filterTab === "grupo" ? (
              <>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Dimensión</Typography>
                <Box sx={{ mb: 1.5 }}>
                  <Box sx={{ display: "flex", border: "1px solid", borderColor: "divider", borderRadius: "4px 4px 0 0", overflow: "hidden", borderBottom: "none" }}>
                    {GROUP_DIM_OPTIONS.slice(0, 3).map((d, i) => (
                      <Button key={d.key} size="small" disableRipple
                        variant={selectedDimension === d.key ? "contained" : "text"} color="primary"
                        onClick={() => setSelectedDimension(d.key)}
                        sx={{ flex: 1, px: 0.3, py: 0.75, fontSize: "0.65rem", textTransform: "none", borderRadius: 0, borderRight: i < 2 ? "1px solid" : "none", borderColor: "divider", boxShadow: "none", minWidth: 0 }}
                      >{d.label}</Button>
                    ))}
                  </Box>
                  <Box sx={{ display: "flex", border: "1px solid", borderColor: "divider", borderRadius: "0 0 4px 4px", overflow: "hidden" }}>
                    {GROUP_DIM_OPTIONS.slice(3).map((d, i) => (
                      <Button key={d.key} size="small" disableRipple
                        variant={selectedDimension === d.key ? "contained" : "text"} color="primary"
                        onClick={() => setSelectedDimension(d.key)}
                        sx={{ flex: 1, px: 0.3, py: 0.75, fontSize: "0.65rem", textTransform: "none", borderRadius: 0, borderRight: i < 2 ? "1px solid" : "none", borderColor: "divider", boxShadow: "none", minWidth: 0 }}
                      >{d.label}</Button>
                    ))}
                  </Box>
                </Box>

                <Box sx={{ mb: 1.5 }}>
                  <InputFilter item="operador" name="Operadores" opt={operadorOpts} value={opFilterValue}
                    disabled={dimDisabled.operador}
                    callback={(_, val) => { if (!val || val === "all") setSelectedOps(operators.map((o) => o.id_operador)); else setSelectedOps([Number(val)]); }}
                  />
                </Box>
                <Box sx={{ mb: 1.5 }}>
                  <InputFilter item="zona" name="Zona" noSort
                    disabled={dimDisabled.zona}
                    opt={[{ value: "all", label: "Todas las zonas" }, ...MOCK_ZONAS.map((z) => ({ value: String(z.id), label: z.label }))]}
                    value={selectedZona} callback={(_, val) => setSelectedZona(val ?? null)}
                  />
                </Box>
                <Box sx={{ mb: 1.5 }}>
                  <InputFilter item="tecnologia" name="Tecnología"
                    disabled={dimDisabled.tec}
                    opt={[{ value: "all", label: "Todas las tecnologías" }, ...tecnologias.map((t) => ({ value: String(t.id), label: t.label }))]}
                    value={selectedTec} callback={(_, val) => setSelectedTec(val ?? null)}
                  />
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
                  {entityKeys.map((k) => (
                    <Chip key={k} label={getEntityLabel(k)} size="small"
                      onDelete={() => handleRemoveEntity(k)}
                      sx={{ fontSize: "0.68rem", bgcolor: "white", border: "1px solid #1976D2", "& .MuiChip-deleteIcon": { color: "#1976D2", "&:hover": { color: "#1565C0" } } }}
                    />
                  ))}
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
                  <TextField size="small" fullWidth label="RBD" value={rbdInput}
                    onChange={(e) => setRbdInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleAddRbd(); }}
                    InputProps={{ startAdornment: <TuneIcon fontSize="small" sx={{ color: "action.active", mr: 1 }} /> }}
                  />
                  <IconButton onClick={handleAddRbd} color="primary"
                    disabled={!rbdInput || selectedRbds.length >= MAX_RBDS}
                  >
                    <AddCircleIcon />
                  </IconButton>
                </Box>
                {selectedRbds.length > 0 && (
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.75, mb: 1, p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
                    {selectedRbds.map((rbd) => (
                      <Chip key={rbd} label={rbd} size="small"
                        deleteIcon={<RemoveCircleOutlineIcon fontSize="small" />}
                        onDelete={() => handleRemoveRbd(rbd)}
                        sx={{ width: "100%", justifyContent: "space-between", fontSize: "0.75rem", fontWeight: 500, bgcolor: "#E3F0FB", color: "#0F69C4", borderRadius: "16px", "& .MuiChip-deleteIcon": { color: "#0F69C4", "&:hover": { color: "#0b4f96" } } }}
                      />
                    ))}
                  </Box>
                )}
                {selectedRbds.length >= MAX_RBDS && (
                  <Typography variant="caption" color="error">Límite completo de RBD a comparar</Typography>
                )}
              </>
            )}

            <Divider sx={{ my: 2 }} />

            {/* ── Ventana ── */}
            {SHOW_VENTANA_FILTER && (
              <>
                <Typography variant="h5" mb={1}>Ventana</Typography>
                <RadioGroup value={ventana} onChange={(e) => setVentana(e.target.value)} sx={{ mb: 1.5 }}>
                  <FormControlLabel value="ven" control={<Radio size="small" />} label={<Typography variant="body2">Ventana Horaria</Typography>} />
                  <FormControlLabel value="dia" control={<Radio size="small" />} label={<Typography variant="body2">Día completo</Typography>} />
                </RadioGroup>

                <Divider sx={{ my: 2 }} />
              </>
            )}

            <Typography variant="subtitle2" fontWeight="bold" mb={1}>Métrica</Typography>
            <Box sx={{ mb: 1.5 }}>
              <InputFilter item="metrica" name="Métrica"
                opt={METRICAS.map((m) => ({ value: m.key, label: m.label }))}
                value={metricaKey} callback={(_, val) => val && setMetricaKey(val)} noSort
              />
            </Box>

            <Typography variant="subtitle2" fontWeight="bold" mb={1}>Visualización</Typography>
            <Box sx={{ mb: 1.5 }}>
              <InputFilter item="visualizacion" name="Visualización"
                opt={VISUALIZACIONES.map((v) => ({ value: v.key, label: v.label }))}
                value={visualizacion} callback={(_, val) => val && setVisualizacion(val)} noSort
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Button variant="outlined" color="error" size="small" fullWidth disableRipple
              startIcon={<RemoveCircleOutlineIcon />} onClick={handleClearFilters}
              sx={{ textTransform: "none" }}
            >
              Limpiar todos los filtros
            </Button>
          </Paper>
        </Box>

        {/* ── Chart ── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {loading ? (
            <HourglassLoader />
          ) : error ? (
            <Paper sx={{ p: 4, minHeight: 200, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 2.5 }}>
              <Alert
                severity="error"
                sx={{ width: "100%", fontSize: "1rem", alignItems: "center", "& .MuiAlert-message": { textAlign: "center", width: "100%" } }}
              >
                {error}
              </Alert>
              <PageButtonBlack onClick={handleRetry} icon={<ReplayIcon />} width="220px">
                Reintentar
              </PageButtonBlack>
            </Paper>
          ) : filterTab === "rbd" && selectedRbds.length === 0 ? (
            <Paper sx={{ p: 4, minHeight: 200, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Typography color="text.secondary">Seleccione al menos un RBD para visualizar datos.</Typography>
            </Paper>
          ) : filteredRows.length === 0 ? (
            <Paper sx={{ p: 4, minHeight: 200, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Typography color="text.secondary">Sin datos para los filtros seleccionados.</Typography>
            </Paper>
          ) : (
          <Paper sx={{ p: 3.5 }}>
            {/* Fila: título (izq) + descripción (der) */}
            <Grid2 container spacing={2} alignItems="flex-start" mb={3}>
              <Grid2 size={{ xs: 12, sm: 4 }}>
                <Typography variant="h5" sx={{ textTransform: "uppercase", lineHeight: 1.3 }}>
                  {selectedMetrica?.label}
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 8 }}>
                <Typography variant="body2" color="text.secondary" fontSize="0.78rem">
                  {selectedMetrica?.description}
                </Typography>
              </Grid2>
            </Grid2>

            <Plot
              data={traces}
              layout={plotLayout}
              config={{ responsive: true, displayModeBar: true, displaylogo: false, modeBarButtonsToRemove: ["toImage"] }}
              style={{ width: "100%" }}
            />

            <Divider sx={{ mt: 1.5, mb: 2 }} />

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button onClick={() => exportXlsx(filteredRows, metricaKey, ventana, selectedMetrica.convert, selectedMetrica.unit, operators)} sx={downloadBtnSx}>
                Descarga tabla (.csv)
              </Button>
            </Box>
          </Paper>
          )}
        </Box>

      </Box>
    </PageBody>
  );
}
